import { useState, useEffect } from 'react';
import { Save, X, Edit2, Trash2, Plus, Search, ChevronLeft, ChevronRight } from 'lucide-react';
import { supabase, isSupabaseConfigured } from '../lib/supabase';
import { Item } from '../types';

interface EditingItem extends Item {
  isNew?: boolean;
}

export function ItemEditor() {
  const [items, setItems] = useState<Item[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editForm, setEditForm] = useState<Partial<EditingItem>>({});
  const [searchQuery, setSearchQuery] = useState('');
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  useEffect(() => {
    fetchItems();
  }, []);

  const fetchItems = async () => {
    if (!isSupabaseConfigured) {
      setLoading(false);
      return;
    }

    try {
      const { data, error } = await supabase
        .from('catalogue_items')
        .select('*')
        .order('category', { ascending: true })
        .order('name', { ascending: true });

      if (error) throw error;
      setItems(data || []);
    } catch (err) {
      console.error('Error fetching items:', err);
      setMessage({ type: 'error', text: 'Failed to load items' });
    } finally {
      setLoading(false);
    }
  };

  const startEditing = (item: Item) => {
    setEditingId(item.id);
    setEditForm({ ...item });
  };

  const cancelEditing = () => {
    setEditingId(null);
    setEditForm({});
    // Remove any unsaved new items
    setItems(items.filter(item => !(item as EditingItem).isNew));
  };

  const saveItem = async () => {
    if (!editForm.name || !editForm.category) {
      setMessage({ type: 'error', text: 'Name and category are required' });
      return;
    }

    setSaving(true);
    setMessage(null);

    try {
      const itemData = {
        name: editForm.name,
        category: editForm.category,
        low_price: editForm.low_price || null,
        medium_price: editForm.medium_price || null,
        high_price: editForm.high_price || null,
        currency: editForm.currency || 'ZAR',
        tags: editForm.tags || [],
        confidence_score: editForm.confidence_score || 0,
        source: editForm.source || 'manual',
      };

      if ((editForm as EditingItem).isNew) {
        // Insert new item
        const { data, error } = await supabase
          .from('items')
          .insert(itemData)
          .select()
          .single();

        if (error) throw error;

        setItems(items.map(item => 
          (item as EditingItem).isNew && item.id === editingId ? data : item
        ));
        setMessage({ type: 'success', text: 'Item created successfully' });
      } else {
        // Update existing item
        const { error } = await supabase
          .from('items')
          .update(itemData)
          .eq('id', editingId);

        if (error) throw error;

        setItems(items.map(item => 
          item.id === editingId ? { ...item, ...itemData } : item
        ));
        setMessage({ type: 'success', text: 'Item updated successfully' });
      }

      setEditingId(null);
      setEditForm({});
    } catch (err) {
      console.error('Error saving item:', err);
      setMessage({ type: 'error', text: 'Failed to save item' });
    } finally {
      setSaving(false);
    }
  };

  const deleteItem = async (id: number) => {
    if (!confirm('Are you sure you want to delete this item?')) return;

    try {
      const { error } = await supabase
        .from('items')
        .delete()
        .eq('id', id);

      if (error) throw error;

      setItems(items.filter(item => item.id !== id));
      setMessage({ type: 'success', text: 'Item deleted successfully' });
    } catch (err) {
      console.error('Error deleting item:', err);
      setMessage({ type: 'error', text: 'Failed to delete item' });
    }
  };

  const addNewItem = () => {
    const newItem: EditingItem = {
      id: Date.now(), // Temporary ID
      name: '',
      category: '',
      low_price: null,
      medium_price: null,
      high_price: null,
      currency: 'ZAR',
      last_updated: new Date().toISOString(),
      source: 'manual',
      sample_urls: [],
      confidence_score: 0,
      tags: [],
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      isNew: true,
    };

    setItems([newItem, ...items]);
    setEditingId(newItem.id);
    setEditForm(newItem);
    setCurrentPage(1);
  };

  // Filter items based on search
  const filteredItems = items.filter(item =>
    item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    item.category.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Pagination
  const totalPages = Math.ceil(filteredItems.length / itemsPerPage);
  const paginatedItems = filteredItems.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const formatPrice = (price: number | null) => {
    if (price === null) return '-';
    return `R ${price.toLocaleString()}`;
  };

  if (!isSupabaseConfigured) {
    return (
      <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
        <p className="text-amber-700 dark:text-amber-300">
          Item editor requires Supabase connection. Please configure your environment variables.
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 border-4 border-gray-200 dark:border-dark-600 border-t-primary-500 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row gap-4 justify-between">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder="Search items..."
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setCurrentPage(1);
            }}
            className="w-full pl-10 pr-4 py-2 bg-gray-50 dark:bg-dark-700 border border-gray-200 dark:border-dark-600 rounded-lg text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>
        <button
          onClick={addNewItem}
          className="px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white font-medium rounded-lg transition-colors flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          Add Item
        </button>
      </div>

      {/* Message */}
      {message && (
        <div
          className={`p-4 rounded-lg ${
            message.type === 'success'
              ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300 border border-green-200 dark:border-green-800'
              : 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 border border-red-200 dark:border-red-800'
          }`}
        >
          {message.text}
        </div>
      )}

      {/* Table */}
      <div className="overflow-x-auto bg-white dark:bg-dark-800 rounded-xl border border-gray-200 dark:border-dark-700">
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-200 dark:border-dark-700 bg-gray-50 dark:bg-dark-700/50">
              <th className="text-left px-4 py-3 text-sm font-medium text-gray-700 dark:text-gray-300">Name</th>
              <th className="text-left px-4 py-3 text-sm font-medium text-gray-700 dark:text-gray-300">Category</th>
              <th className="text-right px-4 py-3 text-sm font-medium text-gray-700 dark:text-gray-300">Low</th>
              <th className="text-right px-4 py-3 text-sm font-medium text-gray-700 dark:text-gray-300">Medium</th>
              <th className="text-right px-4 py-3 text-sm font-medium text-gray-700 dark:text-gray-300">High</th>
              <th className="text-center px-4 py-3 text-sm font-medium text-gray-700 dark:text-gray-300">Actions</th>
            </tr>
          </thead>
          <tbody>
            {paginatedItems.map((item) => (
              <tr
                key={item.id}
                className="border-b border-gray-100 dark:border-dark-700 last:border-0 hover:bg-gray-50 dark:hover:bg-dark-700/30"
              >
                {editingId === item.id ? (
                  // Editing row
                  <>
                    <td className="px-4 py-2">
                      <input
                        type="text"
                        value={editForm.name || ''}
                        onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                        className="w-full px-2 py-1 bg-gray-50 dark:bg-dark-700 border border-gray-200 dark:border-dark-600 rounded text-sm text-gray-900 dark:text-white"
                        placeholder="Item name"
                      />
                    </td>
                    <td className="px-4 py-2">
                      <input
                        type="text"
                        value={editForm.category || ''}
                        onChange={(e) => setEditForm({ ...editForm, category: e.target.value })}
                        className="w-full px-2 py-1 bg-gray-50 dark:bg-dark-700 border border-gray-200 dark:border-dark-600 rounded text-sm text-gray-900 dark:text-white"
                        placeholder="Category"
                      />
                    </td>
                    <td className="px-4 py-2">
                      <input
                        type="number"
                        value={editForm.low_price || ''}
                        onChange={(e) => setEditForm({ ...editForm, low_price: e.target.value ? Number(e.target.value) : null })}
                        className="w-24 px-2 py-1 bg-gray-50 dark:bg-dark-700 border border-gray-200 dark:border-dark-600 rounded text-sm text-right text-gray-900 dark:text-white"
                        placeholder="0"
                      />
                    </td>
                    <td className="px-4 py-2">
                      <input
                        type="number"
                        value={editForm.medium_price || ''}
                        onChange={(e) => setEditForm({ ...editForm, medium_price: e.target.value ? Number(e.target.value) : null })}
                        className="w-24 px-2 py-1 bg-gray-50 dark:bg-dark-700 border border-gray-200 dark:border-dark-600 rounded text-sm text-right text-gray-900 dark:text-white"
                        placeholder="0"
                      />
                    </td>
                    <td className="px-4 py-2">
                      <input
                        type="number"
                        value={editForm.high_price || ''}
                        onChange={(e) => setEditForm({ ...editForm, high_price: e.target.value ? Number(e.target.value) : null })}
                        className="w-24 px-2 py-1 bg-gray-50 dark:bg-dark-700 border border-gray-200 dark:border-dark-600 rounded text-sm text-right text-gray-900 dark:text-white"
                        placeholder="0"
                      />
                    </td>
                    <td className="px-4 py-2">
                      <div className="flex items-center justify-center gap-2">
                        <button
                          onClick={saveItem}
                          disabled={saving}
                          className="p-1.5 text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 rounded transition-colors"
                          title="Save"
                        >
                          <Save className="w-4 h-4" />
                        </button>
                        <button
                          onClick={cancelEditing}
                          className="p-1.5 text-gray-500 hover:bg-gray-100 dark:hover:bg-dark-700 rounded transition-colors"
                          title="Cancel"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </>
                ) : (
                  // Display row
                  <>
                    <td className="px-4 py-3 text-sm text-gray-900 dark:text-white font-medium">
                      {item.name}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                      {item.category}
                    </td>
                    <td className="px-4 py-3 text-sm text-right text-green-600 dark:text-green-400">
                      {formatPrice(item.low_price)}
                    </td>
                    <td className="px-4 py-3 text-sm text-right text-amber-600 dark:text-amber-400">
                      {formatPrice(item.medium_price)}
                    </td>
                    <td className="px-4 py-3 text-sm text-right text-red-600 dark:text-red-400">
                      {formatPrice(item.high_price)}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-center gap-2">
                        <button
                          onClick={() => startEditing(item)}
                          className="p-1.5 text-primary-600 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded transition-colors"
                          title="Edit"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => deleteItem(item.id)}
                          className="p-1.5 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Showing {(currentPage - 1) * itemsPerPage + 1} to{' '}
            {Math.min(currentPage * itemsPerPage, filteredItems.length)} of{' '}
            {filteredItems.length} items
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="p-2 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-dark-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <span className="text-sm text-gray-700 dark:text-gray-300">
              Page {currentPage} of {totalPages}
            </span>
            <button
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="p-2 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-dark-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
